/* The job document and every way to change it.
 *
 * ONE RULE DECIDES THE SHAPE OF THIS FILE: the server owns the geometry.
 *
 * Text edits are optimistic and debounced (8.2.2 — autosave, no Save button).
 * Losing a keystroke to a round trip makes the tool feel broken, and the worst
 * case of a failed text save is a banner and a retry.
 *
 * Geometry edits — boundary, split, merge, reset — are NOT optimistic. They
 * apply whatever the server returns, because the tiling invariant is enforced
 * there and a local guess that disagreed would put a gap on screen that does
 * not exist in the database. The server also refuses moves this client would
 * have allowed (a boundary that empties a neighbour), and those refusals are
 * shown rather than worked around.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { JobDetail, Subtask, SubtaskPatch, SubtaskStatus } from "../api/types";
import { stableMediaUrl } from "./media";

const SAVE_DEBOUNCE_MS = 500; // 8.2.2

export interface JobState {
  job: JobDetail | null;
  loading: boolean;
  error: string | null;
  /** 8.2.2: shown as a non-blocking banner while saves keep failing. */
  saveFailing: boolean;
  reload: () => Promise<void>;

  editText: (subtaskId: number, patch: SubtaskPatch) => void;
  flush: () => Promise<void>;
  moveBoundary: (afterSubtaskId: number, at: number) => Promise<void>;
  split: (subtaskId: number, at: number) => Promise<void>;
  merge: (subtaskId: number) => Promise<void>;
  resetToAi: (subtaskId: number) => Promise<void>;
  /** Title and description of a task. Does not touch its sub-tasks. */
  editTask: (taskId: number,
             patch: { title?: string; description?: string | null }) => Promise<void>;
  review: (subtaskId: number, status: SubtaskStatus, reason?: string) => Promise<void>;

  /** Structural edits only — split and merge. Text edits are not on this stack:
   *  they are debounced field saves the annotator can simply retype, and mixing
   *  the two would make one Undo press mean two different sizes of thing. */
  canUndo: boolean;
  undoLabel: "split" | "merge" | null;
  undo: () => Promise<void>;
}

interface UndoEntry {
  label: "split" | "merge";
  /** Returns nothing: undo always ends in a whole-document reload, because it
   *  changes the tree's shape exactly as the operation it reverses did. */
  run: () => Promise<void>;
}

/** Replace sub-tasks by id, leaving the rest of the document alone. Used by
 *  every write path so the tree is rebuilt in exactly one way. */
function withSubtasks(job: JobDetail, updated: Subtask[]): JobDetail {
  const byId = new Map(updated.map((s) => [s.id, s]));
  return {
    ...job,
    tasks: job.tasks.map((t) => ({
      ...t,
      subtasks: t.subtasks.map((s) => byId.get(s.id) ?? s),
    })),
  };
}

export function useJob(jobId: number, onError: (message: string) => void): JobState {
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveFailing, setSaveFailing] = useState(false);

  // Pending text edits, merged per sub-task. A second keystroke on the same
  // field must not queue a second request — it must replace the first one's
  // payload, or the server sees every intermediate value.
  const pending = useRef<Map<number, SubtaskPatch>>(new Map());
  const timer = useRef<number | null>(null);

  const reload = useCallback(async () => {
    try {
      const fresh = await api.job(jobId);
      // A reload must not restart the video: the presigned media_url is signed
      // anew on every request, and a new <video src> resets the playhead to 0.
      // That is what threw a merge back to T1 · sub-task 1 — see lib/media.ts.
      setJob((prev) => (prev && prev.job_id === fresh.job_id
        ? { ...fresh,
            media_url: stableMediaUrl(prev.media_url, fresh.media_url),
            // 26 Oct 2026: the master (fallback for the 360p copy) and the
            // poster are presigned too — keep them as stable as media_url.
            media_fallback_url: stableMediaUrl(prev.media_fallback_url, fresh.media_fallback_url ?? null),
            poster_url: stableMediaUrl(prev.poster_url, fresh.poster_url ?? null) }
        : fresh));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const flush = useCallback(async () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const batch = [...pending.current.entries()];
    if (!batch.length) return;
    pending.current.clear();

    try {
      const saved = await Promise.all(batch.map(([id, p]) => api.patch(id, p)));
      setSaveFailing(false);
      setJob((j) => {
        if (!j) return j;
        /* KEYSTROKES THAT ARRIVED WHILE THE REQUEST WAS IN FLIGHT WIN.
         *
         * The reply describes the text as it was when the PATCH left — a 500ms
         * debounce plus a round trip ago. Applying it verbatim over a
         * controlled <textarea> reverts everything typed since, so at normal
         * typing speed characters disappeared in bursts and the caret jumped.
         * Testing reported it as "keyboard input is not registering properly",
         * and it was: the input registered and was then overwritten.
         *
         * `pending` is cleared before the request and repopulated by every
         * keystroke since, so it is exactly the set of edits this reply
         * predates. Re-applying it on top keeps the field ahead of the server,
         * which is what an optimistic edit is for; the next flush sends it. */
        const merged = saved.map((s) => {
          const newer = pending.current.get(s.id);
          return newer ? { ...s, ...newer } : s;
        });
        return withSubtasks(j, merged);
      });
    } catch (e) {
      // Put them back so the next flush retries rather than dropping the edit.
      for (const [id, p] of batch)
        pending.current.set(id, { ...p, ...(pending.current.get(id) ?? {}) });
      setSaveFailing(true);
      onError(e instanceof ApiError ? e.message : String(e));
    }
  }, [onError]);

  const editText = useCallback((subtaskId: number, patch: SubtaskPatch) => {
    // Optimistic: the field shows what was typed immediately.
    setJob((j) => {
      if (!j) return j;
      const cur = j.tasks.flatMap((t) => t.subtasks).find((s) => s.id === subtaskId);
      if (!cur) return j;
      // 4.2.7: any edit returns the sub-task to pending, clears its rejection
      // reason and invalidates its last AI check. Mirrored here so the UI does
      // not briefly show an "approved" pill over text that just changed.
      return withSubtasks(j, [{
        ...cur, ...patch,
        status: "PENDING" as SubtaskStatus,
        rejection_reason: null,
        ai_check_valid: false,
        edited: true,
      }]);
    });

    pending.current.set(subtaskId, { ...(pending.current.get(subtaskId) ?? {}), ...patch });
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
  }, [flush]);

  // A pending edit must not be lost to a navigation or a closed tab.
  useEffect(() => () => { void flush(); }, [flush]);

  const geometry = useCallback(
    async (run: () => Promise<Subtask[] | Subtask | void>, whole = false) => {
      await flush(); // ordering: a text edit queued before a split must land first
      try {
        const result = await run();
        if (whole || !result) {
          await reload(); // split/merge change the tree's SHAPE, not just values
          return;
        }
        const list = Array.isArray(result) ? result : [result];
        setJob((j) => (j ? withSubtasks(j, list) : j));
      } catch (e) {
        onError(e instanceof ApiError ? e.message : String(e));
        // The server refused; our copy may have already been drawn optimistically
        // somewhere else, so re-read rather than guess.
        await reload();
      }
    },
    [flush, reload, onError],
  );

  /* ------------------------------------------------------------------ undo */

  /** UNDO IS BUILT FROM THE INVERSE OPERATION, not from a server-side history.
   *
   *  A split and a merge are each other's inverse, so undoing one is performing
   *  the other — no new endpoint, no undo table, and nothing that can drift out
   *  of step with what the database actually holds.
   *
   *  What the inverse does NOT restore is the reason the entry carries a copy
   *  of the old values:
   *
   *    * merge keeps only the survivor's labels and DELETES the follower, so
   *      splitting back produces a second half carrying a copy of the first's
   *      description, verb, object — plausible, and not what was there.
   *    * both operations reset status to PENDING and clear ai_check_valid, so
   *      an approved sub-task comes back unapproved unless the decision is
   *      replayed.
   *
   *  So each entry stores the fields and the decisions, and undo replays them
   *  after the structural step. That is why this is a stack of closures over
   *  captured state rather than a list of ids.
   *
   *  It is per-session and in memory. A reload loses it — correct, because
   *  after a reload the ids in these closures may name rows that no longer
   *  exist, and an undo that silently edits the wrong sub-task is worse than
   *  no undo at all. */
  const [undoDepth, setUndoDepth] = useState(0);
  const undoStack = useRef<UndoEntry[]>([]);

  const pushUndo = (entry: UndoEntry) => {
    undoStack.current.push(entry);
    setUndoDepth(undoStack.current.length);
  };

  /** The sub-task's restorable content — everything a structural edit can lose. */
  const snapshot = (s: Subtask): SubtaskPatch => ({
    custom_title: s.custom_title, description: s.description,
    verb: s.verb, object_name: s.object_name, tool: s.tool, target: s.target,
    hands: s.hands,
  });

  const findSub = (id: number): { sub: Subtask; siblings: Subtask[] } | null => {
    for (const t of job?.tasks ?? []) {
      const sub = t.subtasks.find((s) => s.id === id);
      if (sub) return { sub, siblings: t.subtasks };
    }
    return null;
  };

  /** Put a decision back after the structure has been rebuilt. */
  const replay = async (id: number, status: SubtaskStatus, reason: string | null) => {
    if (status === "PENDING") return;
    await api.review(id, status, reason ?? undefined);
  };

  return {
    job, loading, error, saveFailing, reload, editText, flush,
    canUndo: undoDepth > 0,
    undoLabel: undoStack.current[undoStack.current.length - 1]?.label ?? null,

    moveBoundary: (afterSubtaskId, at) =>
      geometry(() => api.moveBoundary(jobId, afterSubtaskId, at)),

    // Whole-document reload: these add or remove rows and renumber the rest.
    split: async (subtaskId, at) => {
      const found = findSub(subtaskId);
      const before = found?.sub;
      await geometry(async () => {
        const halves = await api.split(subtaskId, at);
        if (before) {
          const [left] = halves;
          pushUndo({
            label: "split",
            // Undo: absorb the right half back into the left, then put the
            // original's decision back — merge always leaves it PENDING.
            run: async () => {
              await api.merge(left.id);
              await api.patch(left.id, snapshot(before));
              await replay(left.id, before.status, before.rejection_reason);
            },
          });
        }
        return halves;
      }, true);
    },

    merge: async (subtaskId) => {
      const found = findSub(subtaskId);
      const survivor = found?.sub;
      const absorbed = found
        ? found.siblings[found.siblings.findIndex((s) => s.id === subtaskId) + 1]
        : undefined;
      await geometry(async () => {
        const result = await api.merge(subtaskId);
        if (survivor && absorbed) {
          const boundary = absorbed.start_seconds;
          pushUndo({
            label: "merge",
            // Undo: cut at the boundary that was erased, then restore BOTH
            // halves — the right one is a copy of the left until it is patched.
            run: async () => {
              const [left, right] = await api.split(result.id, boundary);
              await api.patch(right.id, snapshot(absorbed));
              await api.patch(left.id, snapshot(survivor));
              await replay(left.id, survivor.status, survivor.rejection_reason);
              await replay(right.id, absorbed.status, absorbed.rejection_reason);
            },
          });
        }
        return result;
      }, true);
    },

    undo: async () => {
      const entry = undoStack.current.pop();
      setUndoDepth(undoStack.current.length);
      if (!entry) return;
      await geometry(() => entry.run(), true);
    },

    // A task edit is not a geometry edit and not a sub-task edit: it touches
    // no span and resets nothing to PENDING (see services.patch_task). It still
    // reloads, because the title appears in three places — the strip, the card
    // and the player overlay — and they must not disagree.
    editTask: async (taskId, patch) => {
      try {
        await api.patchTask(taskId, patch);
        await reload();
      } catch (e) {
        onError(e instanceof ApiError ? e.message : String(e));
      }
    },

    resetToAi: (subtaskId) => geometry(() => api.reset(subtaskId)),
    review: (subtaskId, status, reason) =>
      geometry(() => api.review(subtaskId, status, reason)),
  };
}
